/**
 * Stage S11 — `processCommands`, the GENERIC half.
 *
 * Source:  (`lc:` citations below). Spec: `docs/spec/00-MASTER-SPEC.md`
 * §3.9, rules R-142 … R-279. This module owns every row of §3.9 that is NOT a Bahá'í domain
 * macro; a sibling module owns `\ts`, `\appel*`, `\citation`, `\Nverset`/`\Npar`/`\Nhidden`,
 * `\afficherTitre`/`\afficherSommaire` and the endnote placeholders.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────
 * ORDER IS THE CONTRACT.
 * §3.9 is a single `let html = latex` threaded through 138 sequential `.replace()` calls.
 * Each one sees the *output* of all the previous ones, and most of the confirmed defects in
 * `docs/DEFERRED-FIXES.md` are order-of-operations consequences. The exported functions below
 * are the source's contiguous runs, in source order; running them out of order changes the
 * emitted scripture. The full order — including the domain slots this module does not own —
 * is documented in `S11_ORDER` at the bottom of this file.
 * ─────────────────────────────────────────────────────────────────────────────────────────
 *
 * REPRODUCE BUG-FOR-BUG (CLAUDE.md §3). Defects deliberately preserved here:
 *   DF-6  `\\%` → `\%` — comment text is published as scripture, plus a stray backslash.
 *   DF-10 accents (`\'e`, `` \`a ``, `\^o`, `\"u`, `\~n`, `\=o`) and ligatures (`\oe`, `\ae`,
 *         `\ss`) have NO rule and print as raw LaTeX. `\~` is additionally mangled to
 *         `\&nbsp;` by the global `~` rule. Do not add a rule for these.
 *   G-16  `\vspace` injects its LaTeX length raw; `\hspace` converts it. `12pt` therefore
 *         becomes `margin-top: 12pt` but `margin-left: 16px`.
 *   G-17  `\enspace` is replaced twice; the second pass is a no-op.
 *   R-221 `--` → `–` runs at S11, so `[label=--]` can never be seen by S12.
 *   R-253 `/\\ /g` → `' '` destroys a table row separator written `\\ ` (DF-3).
 *   R-222 global `~` → `&nbsp;` destroys `\arbsq` (DF-14) and mangles `\~`.
 *   R-206 `\$` → `$` fires far too late: S10 already consumed it (DF-5).
 *
 * Pure functions only — no I/O, no DOM, no module-level mutable state.
 */

import { extractBracedContent } from './braces'
import type { DocumentMetadata } from './types'

// ─────────────────────────────────────────────────────────────────────────────────────────
// Shared primitives
// ─────────────────────────────────────────────────────────────────────────────────────────

/**
 * R-019 — `latexLengthToCSS`.
 *
 * PRIVATE COPY. The settings stage owns the canonical R-019; `\hspace` (R-257) needs it here
 * and this module must not depend on another agent's file. The two are byte-identical
 * conversions — if the coordinator prefers one home, import it and delete this.
 *
 * `pt` → px uses raw JS float division: `25pt` → `33.33333333333333px`, `2pt` →
 * `2.6666666666666665px`, `12pt` → `16px`. That imprecision is observable in the output and
 * is therefore part of the contract.
 */
function latexLengthToCSS(length: string): string {
  const trimmed = length.trim()
  if (trimmed.endsWith('mm')) return trimmed
  if (trimmed.endsWith('cm')) return trimmed
  if (trimmed.endsWith('in')) return trimmed
  if (trimmed.endsWith('pt')) {
    const val = parseFloat(trimmed)
    return `${(val / 72) * 96}px` // 72pt = 1in = 96px
  }
  if (trimmed.endsWith('em')) return trimmed
  if (trimmed.endsWith('ex')) return trimmed
  const num = parseFloat(trimmed)
  if (!isNaN(num)) return `${num}pt`
  return trimmed
}

/**
 * `extractCommandContent` — first `\cmd{...}` body, brace-matched.
 *
 * Non-global match, so only the FIRST occurrence is ever returned. Used by S4 metadata
 * capture; exported here because it is a command-stage primitive.
 */
export function extractCommandContent(text: string, command: string): string | null {
  const regex = new RegExp(`\\\\${command}\\s*\\{`)
  const match = text.match(regex)
  if (!match || match.index === undefined) return null

  const startBrace = match.index + match[0].length - 1
  const result = extractBracedContent(text, startBrace)
  return result ? result.content : null
}

/**
 * `removeCommand` — delete every `\cmd{...}`, command AND braced body.
 *
 * The source builds a NON-global regex and re-`exec`s it against the shrinking string, so
 * every pass restarts from index 0. Stops at the first unbalanced brace (`break`), leaving
 * that occurrence and everything after it in place.
 */
export function removeCommand(text: string, command: string, arity = 1): string {
  const regex = new RegExp(`\\\\${command}\\s*\\{`)
  let result = text
  let match: RegExpExecArray | null

  while ((match = regex.exec(result)) !== null) {
    // `arity` consecutive balanced groups. Default 1, so every existing caller
    // (R-145/146/147, R-277) behaves exactly as before.
    let cursor = match.index + match[0].length - 1
    let lastEnd = -1
    let complete = true

    for (let i = 0; i < arity; i++) {
      if (result.charAt(cursor) !== '{') {
        complete = false
        break
      }
      const braced = extractBracedContent(result, cursor)
      if (!braced) {
        complete = false
        break
      }
      lastEnd = braced.endIndex
      cursor = braced.endIndex + 1
      // Between arguments only, skip horizontal space. NOT after the last one — the original
      // sliced from `endIndex + 1`, so trailing whitespace is preserved and output is unchanged.
      if (i < arity - 1) {
        while (cursor < result.length && /[ \t]/.test(result.charAt(cursor))) cursor++
      }
    }

    if (!complete) break
    result = result.substring(0, match.index) + result.substring(lastEnd + 1)
  }
  return result
}

/**
 * `replaceCommandWithTag` — `\cmd{body}` → `<tag class="cls">body</tag>`.
 *
 * Nested-brace safe (chunk-builder + `extractBracedContent`), unlike the naive `[^}]*`
 * duplicates at. `\cmd` and `{` may be separated by any whitespace, INCLUDING a
 * newline (`\s*`). On an unbalanced brace the matched `\cmd{` is re-emitted verbatim and
 * scanning continues past it — that is why `\textbf{abc` survives unchanged.
 *
 * The body is copied verbatim: no HTML escaping anywhere (DF-9).
 *
 * DUPLICATE — `commands-domain.ts` exports an identical `replaceCommandWithTag` for its own
 * slot-4 rules. Both modules need it and neither should depend on the other, so both keep a
 * copy; pick one when wiring `index.ts`.
 */
export function replaceCommandWithTag(
  text: string,
  command: string,
  tag: string,
  className?: string,
): string {
  const pattern = new RegExp(`\\\\${command}\\s*\\{`, 'g')
  let result = ''
  let lastIndex = 0
  let match: RegExpExecArray | null

  while ((match = pattern.exec(text)) !== null) {
    result += text.substring(lastIndex, match.index)

    const startBrace = match.index + match[0].length - 1
    const braced = extractBracedContent(text, startBrace)

    if (braced) {
      const content = braced.content
      const attrs = className ? ` class="${className}"` : ''
      result += `<${tag}${attrs}>${content}</${tag}>`
      lastIndex = braced.endIndex + 1
      pattern.lastIndex = lastIndex
    } else {
      result += match[0]
      lastIndex = match.index + match[0].length
      pattern.lastIndex = lastIndex
    }
  }

  result += text.substring(lastIndex)
  return result
}

/**
 * The separator emitted for every page break (R-238 … R-240). Also needed by S19/S21.
 *
 * SELF-CONTAINED BY REQUIREMENT — owner ruling, 2026-08-30. It used to be
 * `<img src="/divider.svg">`, and **that file has never existed in this repository**:
 * not in `public/` (which does not exist), not in `dist/`, never committed at any point
 * in `git log --all`. It was inherited from the predecessor app and the asset never came
 * with it, so every page break published a broken image — roughly 49 of them across the
 * eight loaded documents, since four house macros each contain a `\newpage`
 * (`\afficherTitre`, `\afficherSommaire`, `\afficherEndNotes`, `\afficherComTrad`).
 *
 * The replacement is an inline SVG fleuron — a pair of leaves flanking a centre dot,
 * with hairlines running out to either side. Drawn rather than borrowed from a font,
 * because the obvious alternatives (`❦` U+2766, `⁂` U+2042) are not in Merriweather and
 * would fall back silently to another face or a blank box — the same fragility as the
 * missing file, only less visible.
 *
 * Four properties this shape must keep:
 *   • **No external reference.** The published HTML is uploaded to a library and must
 *     render there with nothing alongside it. Nothing may be fetched.
 *   • **`currentColor` throughout.** It inherits the text colour, so it works on any
 *     ground, in either theme, and in print.
 *   • **`width`/`height` present as well as `viewBox`**, so it still renders at a sane
 *     size if a fragment is pasted somewhere without the stylesheet.
 *   • **`aria-hidden`.** It is decoration; a screen reader must not announce it.
 *
 * /!\ THIS STRING IS MATCHED BY REGEX ELSEWHERE. `paragraphs.ts` builds its S19 pattern
 * from this constant rather than repeating the markup, so the two cannot drift — the old
 * markup was hard-coded there, and changing this line alone would have silently stopped
 * that rule matching. Keep it that way.
 */
export const PAGE_BREAK_ORNAMENT =
  '<svg class="page-break-ornament" width="200" height="26" viewBox="0 0 200 26" ' +
  'role="presentation" aria-hidden="true" focusable="false">' +
  '<g fill="currentColor">' +
  '<path d="M92 13c0-5-4-8-9-9 1 5 4 8 9 9zM92 13c0 5-4 8-9 9 1-5 4-8 9-9z"/>' +
  '<path d="M108 13c0-5 4-8 9-9-1 5-4 8-9 9zM108 13c0 5 4 8 9 9-1-5-4-8-9-9z"/>' +
  '<circle cx="100" cy="13" r="2.1"/>' +
  '</g>' +
  '<g stroke="currentColor" stroke-width=".8" stroke-linecap="round" opacity=".45">' +
  '<path d="M34 13h44M122 13h44"/>' +
  '</g>' +
  '</svg>'

/** R-238 … R-240 — what `\newpage`, `\clearpage` and `\pagebreak` all become. */
export const PAGE_BREAK_HTML = `<div class="page-break">${PAGE_BREAK_ORNAMENT}</div>`

// ─────────────────────────────────────────────────────────────────────────────────────────
// Slot 1 — preamble command removal (R-142 … R-149, )
// ─────────────────────────────────────────────────────────────────────────────────────────

/**
 * R-142 … R-149. Deletes `\documentclass`, `\usepackage`, `\input`, `\title`, `\author`,
 * `\date`, `\begin{document}`, `\end{document}`.
 *
 * `\title`/`\author`/`\date` go through `removeCommand`, so nested braces in their bodies are
 * handled; the other three use `[^}]+` and are not.
 */
export function removePreambleCommands(html: string): string {
  let out = html
  out = out.replace(/\\documentclass(\[[^\]]*\])?\{[^}]+\}/g, '') // R-142
  // PREAMBLE LINK: R-143 is the single reason NO \usepackage line in preambuleV3.tex is ever
  out = out.replace(/\\usepackage(\[[^\]]*\])?\{[^}]+\}/g, '') // R-143
  out = out.replace(/\\input\{[^}]+\}/g, '') // R-144
  out = removeCommand(out, 'title') // R-145
  out = removeCommand(out, 'author') // R-146
  out = removeCommand(out, 'date') // R-147
  out = out.replace(/\\begin\{document\}/g, '') // R-148
  out = out.replace(/\\end\{document\}/g, '') // R-149
  out = out.replace(/\\input\{[^}]+\}/g, '') // R-144
  out = removeCommand(out, 'title') // R-145
  out = removeCommand(out, 'author') // R-146
  out = removeCommand(out, 'date') // R-147
  out = out.replace(/\\begin\{document\}/g, '') // R-148
  out = out.replace(/\\end\{document\}/g, '') // R-149
  return out
}

// ─────────────────────────────────────────────────────────────────────────────────────────
// Slot 2 — \maketitle (R-150 / R-078 / R-080, )
// ─────────────────────────────────────────────────────────────────────────────────────────

/**
 * Author size detection shared by `\maketitle` and the Mode-B `\afficherTitre{}`
 * handler. The two blocks are byte-identical in the source.
 *
 * `if / else if` chain — the FIRST of `\LARGE`, `\Large`, `\large` present wins and only that
 * one is stripped; the others stay as literal LaTeX in the author line.
 */
export function buildAuthorParagraph(authorContent: string): string {
  let authorClass = 'author'
  let processedAuthor = authorContent

  if (processedAuthor.includes('\\LARGE')) {
    authorClass += ' LARGE-text'
    processedAuthor = processedAuthor.replace(/\\LARGE\s*/g, '')
  } else if (processedAuthor.includes('\\Large')) {
    authorClass += ' Large-text'
    processedAuthor = processedAuthor.replace(/\\Large\s*/g, '')
  } else if (processedAuthor.includes('\\large')) {
    authorClass += ' large-text'
    processedAuthor = processedAuthor.replace(/\\large\s*/g, '')
  }

  return `<p class="${authorClass}">${processedAuthor}</p>`
}

/**
 * R-150 (§3.5 R-078/R-080) — `\maketitle`,.
 *
 * G-25, HALF ONE: the date here renders **en-US** (`July 28, 2026`) — and only when the raw
 * `\date{}` body trims to exactly `\today`. The body `\today` at R-227 renders **fr-FR**. Two
 * locales in one French document; both are reproduced.
 *
 * Emits, in order and only when non-empty: `<h1 class="title no-toc">`, `<p class="author…">`,
 * `<p class="date">`. `\par` inside the title becomes `<br>` — note `\par\s*`, which
 * eats the following whitespace, unlike the body rule R-237.
 *
 * A function replacer, so `$&`/`` $` ``/`$'`/`$$` inside the metadata are inserted literally.
 *
 * @param now injected clock. Defaults to `new Date()`, exactly as.
 */
export function processMaketitle(
  html: string,
  metadata: DocumentMetadata,
  now: Date = new Date(),
): string {
  const titleContent = metadata.title
  const authorContent = metadata.author
  const dateContent = metadata.date

  return html.replace(/\\maketitle/g, () => {
    let titleBlock = ''
    if (titleContent) {
      const processedTitle = titleContent.replace(/\\par\s*/g, '<br>')
      titleBlock += `<h1 class="title no-toc">${processedTitle}</h1>`
    }
    if (authorContent) {
      titleBlock += buildAuthorParagraph(authorContent)
    }
    if (dateContent) {
      // en-US. G-25.
      const dateVal =
        dateContent.trim() === '\\today'
          ? now.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
          : dateContent
      titleBlock += `<p class="date">${dateVal}</p>`
    }
    return titleBlock
  })
}

// ─────────────────────────────────────────────────────────────────────────────────────────
// Slot 3 — text formatting (R-151 … R-157, )
// ─────────────────────────────────────────────────────────────────────────────────────────

/**
 * R-151 … R-157. The nested-brace-safe formatting pass, run BEFORE structure commands so that
 * `\section{\textbf{…}}` works.
 *
 * ORDER NOTE: `textit` (R-152) precedes `emph` (R-155), so `\emph{\textit{x}}` nests as
 * `<em><em>x</em></em>`.
 *
 * NOT HANDLED, deliberately (R-324): `\textsubscript` — it has no rule anywhere and prints as
 * raw LaTeX even though `\textsuperscript` is handled one line above. Do not add it.
 */
/**
 * R-156b — the lettrine: `{\LARGE X}\textsc{Y}` → `.ltr-initial` + `.ltr-rest`.
 *
 * PREAMBLE LINK: `\ltrPar` (preambuleV3.tex:500) and `\ltrNum` (:502) — *"Lettrine (ltr):
 * première lettre en taille LARGE et lettres suivantes en petites capitales (sc)"*.
 * FIXED 2026-08-31; before that both were broken in two ways at once.
 *
 * ```
 * \ltrPar{Ô}{ peuples}     expands at S7 to   {\LARGE Ô}\textsc{ peuples}
 *   was published as       {&nbsp;Ô}<span class="small-caps"> peuples</span>
 *   now                    <span class="ltr-initial">Ô</span><span class="ltr-rest"> peuples</span>
 * ```
 *
 * **Why it was broken.** The converter understood only `\LARGE{X}` — the argument form (R-195).
 * The preamble writes `{\LARGE X}`, which is LaTeX's *actual* construct: a group with a size
 * switch inside it. R-202 matches a bare `\LARGE` not followed by `{` and DELETES it, so the
 * size vanished and the group's braces were left behind as literal text (R-348).
 *
 * **Why this runs here, at slot 3, and not with the other size rules at slot 9.** `\textsc` is
 * converted on the very next line. Matching after that would mean matching generated HTML
 * (`<span class="small-caps">…`), which is brittle; matching here works on the source shape.
 *
 * `.ltr-initial` and `.ltr-rest` already existed in `css.ts` and were **emitted by nothing** —
 * this rule is what makes them live. Anything that is not this exact pair still falls through
 * to the ordinary `\textsc` and font-size rules, unchanged.
 *
 * **A9b — FIXED 2026-08-31, and this is why the flat regex went.** The first implementation
 * matched with `[^{}]*` in both halves, so a lettrine whose argument contained *any* markup
 * put a brace inside the span the character class could not cross, and the rule **silently did
 * not fire** — no error, no log, just `{ Ô}` back in the page and the tail falling through to
 * an ordinary `.small-caps`. `\ltrPar{S}{ha\ts{y}\ts{kh}}` was enough to trigger it, and `\ts`
 * (the transliteration overbar) is the house spelling for every Arabic and Persian name in the
 * corpus — so a line that worked would start publishing braces the day a chapter opened on a
 * transliterated word, with nothing to explain it. The `SILENT-BREAKAGE.md` class exactly.
 *
 * It now scans with `extractBracedContent`, the same balanced-brace helper
 * {@link replaceCommandWithTag} uses 250 lines above — the file's own idiom for this, not a new
 * one. Both arguments accept nested markup; only whitespace may sit between the two halves, so
 * `{\LARGE Ô} et puis \textsc{peuples}` still does not match.
 *
 * **Deliberately NOT fixed here (A9c):** a `{\LARGE …}` group that is not followed by `\textsc`
 * is still unhandled — `{\LARGE seul}` publishes `{ seul}`, braces and all, because R-202
 * deletes the switch and nothing consumes the group. That is the size-switch defect in its
 * general form and it needs its own decision; keeping it out is what made this change
 * measurable (148 fixture conversions and all 8 corpus documents byte-identical).
 */
export function processLettrine(html: string): string {
  // R-156b. Balanced extraction, NOT `[^{}]*` — see A9b in the note above.
  const open = /\{\\LARGE\s+/g
  const TEXTSC = '\\textsc'
  let result = ''
  let last = 0
  let match: RegExpExecArray | null

  while ((match = open.exec(html)) !== null) {
    // The initial: the `{…}` group whose content begins with the size switch.
    const initial = extractBracedContent(html, match.index)
    if (!initial) continue
    const initialText = html.substring(match.index + match[0].length, initial.endIndex)

    // The tail must follow immediately — whitespace only, so that text between the two halves
    // still declines to match (pinned by a test).
    let cursor = initial.endIndex + 1
    while (cursor < html.length && /\s/.test(html.charAt(cursor))) cursor++
    if (!html.startsWith(TEXTSC + '{', cursor)) continue

    const rest = extractBracedContent(html, cursor + TEXTSC.length)
    if (!rest) continue

    result += html.substring(last, match.index)
    result += `<span class="ltr-initial">${initialText}</span>`
    result += `<span class="ltr-rest">${rest.content}</span>`
    last = rest.endIndex + 1
    open.lastIndex = last
  }

  return result + html.substring(last)
}

/**
 * A8b — the Kitáb-i-Aqdas apparatus stops publishing its argument's braces.
 *
 * PREAMBLE LINK: `\KAquestion` (preambuleV3.tex:586) and `\KAreponse` (:609).
 *
 * ```latex
 * \newcommand{\KAquestion}[1]{\textit{\textbf{Question}~: {#1}}}
 * \newcommand{\KAreponse}[1]{\par\noindent\textit{Réponse}~: {#1}}
 * ```
 *
 * Both wrap the argument in a **bare group**, which prints nothing in LaTeX. This converter has
 * no model of a group, so after expansion the braces reached the page:
 * `Question&nbsp;: {À propos de la Très-Grande-Fête.}` — the question text in braces, in
 * scripture. Catalogued as **A8b** in `docs/PREAMBLE-NOT-HONOURED.md`.
 *
 * **Why R-348 could not be reused.** `stripCallWordGroupBraces` keys on the idiom the five
 * `\appel*` share — a bare group immediately followed by `\nopagebreak`. `\KAquestion` ends
 * `{#1}}` and `\KAreponse` ends `{#1}` with nothing after, so neither offers that anchor.
 *
 * **The anchor chosen, and the evidence for it.** The rule keys on the two literal openings the
 * preamble itself writes, each followed by `~:` and a bare group. In the 2026-08-09 corpus
 * `~: {` occurs **0 times**, so this cannot fire on translator prose, while `\textbf{Question}`
 * occurs **103 times** — all of them the hand-written longhand form *without* braces, which this
 * rule leaves untouched because it requires the `{`.
 *
 * ⚠️ **The failure mode of this anchor is a silent one**, so it is guarded loudly: if a future
 * preamble renames "Question" or "Réponse", this rule stops firing with no error. A test in
 * `ka-apparatus.test.ts` asserts the two `\newcommand` bodies still contain these exact strings,
 * so a rename fails the suite instead of quietly restoring the defect.
 *
 * **Why it lives here, at slot 3, rather than with the other domain rules at slot 4.** By slot 4
 * `\textbf` and `\textit` have already become `<strong>` / `<em>`, so the anchor would have to
 * match generated HTML. Matching the source shape is the same reasoning that puts
 * {@link processLettrine} here (R-156b).
 *
 * Balanced extraction from the outset — the A9b lesson. `\KAquestion{le verset \textbf{X}}` has a
 * nested brace, and a `[^{}]*` rule would silently not fire on exactly the kind of line an
 * editor is most likely to write.
 */
export function stripKAApparatusBraces(html: string): string {
  // A8b. The two openings are quoted from preambuleV3.tex:586 and :609.
  const anchors = [/\\textbf\{Question\}~:\s*/g, /\\textit\{Réponse\}~:\s*/g]

  let out = html
  for (const anchor of anchors) {
    let result = ''
    let last = 0
    let match: RegExpExecArray | null
    anchor.lastIndex = 0

    while ((match = anchor.exec(out)) !== null) {
      const braceStart = match.index + match[0].length
      if (out.charAt(braceStart) !== '{') continue

      const group = extractBracedContent(out, braceStart)
      if (!group) continue

      // The braces go; the content stays where it was. No `{}` placeholder is needed here —
      // unlike R-348, the group follows `~: ` (a space), so removing it cannot glue the text
      // onto a preceding command name and cost a later rule its word boundary.
      result += out.substring(last, braceStart)
      result += group.content
      last = group.endIndex + 1
      anchor.lastIndex = last
    }
    out = result + out.substring(last)
  }
  return out
}

/**
 * The class each size switch maps to. Irregular on purpose — these are the names `css.ts` has
 * carried since the port, and `scriptsize` / `footnotesize` genuinely have no `-text` suffix.
 * `\normalsize` maps to nothing: it is the body size, so the group is simply unwrapped.
 */
const SIZE_CLASS: Readonly<Record<string, string>> = {
  tiny: 'tiny-text',
  scriptsize: 'scriptsize',
  footnotesize: 'footnotesize',
  small: 'small-text',
  normalsize: '',
  large: 'large-text',
  Large: 'Large-text',
  LARGE: 'LARGE-text',
  huge: 'huge-text',
  Huge: 'Huge-text',
}

/**
 * Commands that take NO argument, so a `{` after one opens a *bare group*, not an argument.
 * This is R-348's list, reused deliberately: it was derived from the `\appel*` bodies and is the
 * only place in this codebase that already answers "is this brace an argument?".
 */
const ARGUMENT_LESS = new Set(['par', 'bigskip', 'medskip', 'smallskip', 'hfil', 'hfill', 'noindent'])

/**
 * Commands whose braced content ALREADY has its own size handling elsewhere, and which this rule
 * deliberately does not take over. See the note on {@link processSizeGroups}.
 */
const SIZE_HANDLED_ELSEWHERE = new Set(['marginpar', 'author', 'title', 'date'])

/** Is the `{` at `braceIndex` a bare group rather than a command's argument? */
function isBareGroup(text: string, braceIndex: number): { bare: boolean; owner: string } {
  let j = braceIndex - 1
  while (j >= 0 && (text.charAt(j) === ' ' || text.charAt(j) === '\t')) j--
  if (j < 0) return { bare: true, owner: '' }

  let c = text.charAt(j)
  if (c === '*') {
    j--
    c = text.charAt(j)
  }
  // `}` = a second argument; `]` = follows an optional-argument group. Neither is a bare group.
  if (c === '}' || c === ']') return { bare: false, owner: '' }

  if (/[A-Za-z@]/.test(c)) {
    let k = j
    while (k >= 0 && /[A-Za-z@]/.test(text.charAt(k))) k--
    if (text.charAt(k) === '\\') {
      const name = text.substring(k + 1, j + 1)
      return { bare: ARGUMENT_LESS.has(name), owner: name }
    }
    return { bare: true, owner: '' } // ordinary word before the brace
  }
  if (c === '\\') return { bare: false, owner: '' } // `\{`, or a backslash we cannot read
  return { bare: true, owner: '' }
}

/**
 * A14 / A9c — a size switch is scoped to its enclosing group, the way LaTeX scopes it.
 *
 * PREAMBLE LINK: `\appelCBL` (preambuleV3.tex:431) and `\appelCRLS` (:456) — *"taille Large"*.
 *
 * ⚠️ Line numbers in this file drift every time a `% CONVERTISSEUR:` note is added to the
 * preamble — three of the citations above were already stale by the end of the session that
 * wrote them. **The macro name is the durable half of the citation; the number is a
 * convenience.** Verify with `grep -n 'newcommand{\\appelCBL}' preambuleV3.tex` rather than
 * trusting the digits.
 *
 * ```latex
 * \newcommand{\appelCBL}[1]{\par\noindent\hfil\textbf{\Large #1}\nopagebreak\smallskip}
 * \newcommand{\appelCRLS}[1]{\par\noindent\hfil{\Large #1}\nopagebreak\bigskip}
 * ```
 *
 * **The concept the converter was missing.** `{\Large text}` is LaTeX's real construct: a group
 * with a size switch scoped inside it. The engine understood only `\Large{text}` — the *argument*
 * form (R-194), which is not how LaTeX works — and R-201 DELETED a bare `\Large`, taking the size
 * with it and leaving the space that followed. A call word asking to be big published at body size
 * with a stray space in front of it, and a bare `{\LARGE X}` published its braces too (A9c).
 *
 * **This is one concept that had five implementations**, R-191…R-196, R-198…R-203,
 * {@link buildAuthorParagraph}, `notes.ts` `processMarginpar`, and R-156b — one of them dead code
 * (`processMarginpar`'s `\scriptsize` branch can never fire; the token is deleted eight slots
 * earlier — C-1). Rather than add a sixth shape-match, this rule implements the concept.
 *
 * **Two outputs, because a brace means two different things.**
 * - A **bare group** (`{\Large X}`, or after an argument-less command such as `\hfil`) prints
 *   nothing in LaTeX, so the braces are consumed: `<span class="Large-text">X</span>`.
 * - A **command's argument** (`\textbf{\Large X}`) must keep its braces or the command loses its
 *   argument, so the span goes inside: `\textbf{<span class="Large-text">X</span>}`.
 *
 * Telling them apart is the same question R-348 answered by naming the argument-less commands
 * explicitly; {@link ARGUMENT_LESS} is that list, reused rather than re-derived.
 *
 * **Ordering — three constraints, all load-bearing.**
 * 1. AFTER {@link processLettrine}, which consumes `{\LARGE X}\textsc{Y}` as a unit. Running first
 *    would rewrite the cap and the lettrine would never match.
 * 2. BEFORE R-151/R-152 convert `\textbf`/`\textit`, so `\textbf{\Large X}` still has braces to
 *    scope to.
 * 3. BEFORE R-348 at slot 4, which strips `\hfil{…}` before `\nopagebreak` — it would otherwise
 *    destroy `\appelCRLS`'s group before this rule could see it.
 *
 * ⚠️ **DELIBERATELY NOT TAKEN OVER**, and this is the whole reason the change stays measurable:
 * `\marginpar`, `\author`, `\title`, `\date` ({@link SIZE_HANDLED_ELSEWHERE}). Those already hoist
 * their size to a CSS class in `processMarginpar` / `buildAuthorParagraph`. Taking them would put a
 * `.scriptsize` span INSIDE `.marginpar`, and because `em` compounds, 0.7em × 0.7em would render
 * every one of the 2057 `\Nverset` numbers at half size unless `.marginpar`'s hard-coded font-size
 * were removed in the same breath. That consolidation is a real improvement — it would replace two
 * hard-coded CSS values with honoured preamble links and delete the dead C-1 branch — but it is its
 * own change with its own blast radius, and it is not this one.
 *
 * ⚠️ **Still not handled, and out of scope:** a size switch that is NOT at the start of its group,
 * e.g. `\afficherComTrad`'s `…\par\bigskip\small\textit{…}` (A12). R-199 still deletes that one.
 */
export function processSizeGroups(html: string): string {
  // A14 / A9c
  const opener = /\{\s*\\(tiny|scriptsize|footnotesize|small|normalsize|large|Large|LARGE|huge|Huge)\b[ \t]*/g

  let result = ''
  let last = 0
  let match: RegExpExecArray | null
  opener.lastIndex = 0

  while ((match = opener.exec(html)) !== null) {
    const braceStart = match.index
    const group = extractBracedContent(html, braceStart)
    if (!group) continue

    const { bare, owner } = isBareGroup(html, braceStart)
    if (SIZE_HANDLED_ELSEWHERE.has(owner)) continue

    const size = match[1] as string
    const className = SIZE_CLASS[size] ?? ''
    const content = html.substring(match.index + match[0].length, group.endIndex)
    const span = className ? `<span class="${className}">${content}</span>` : content

    result += html.substring(last, braceStart)
    result += bare ? span : `{${span}}`
    last = group.endIndex + 1
    opener.lastIndex = last
  }

  return result + html.substring(last)
}

export function processTextFormatting(html: string): string {
  let out = html
  // R-156b — MUST precede R-156, which would otherwise consume the `\textsc` half.
  out = processLettrine(out)
  // A8b — MUST precede R-151/R-152, which turn the anchors into <strong>/<em>.
  out = stripKAApparatusBraces(out)
  // A14 / A9c — MUST follow processLettrine and precede R-151/R-152. See the note above.
  out = processSizeGroups(out)
  out = replaceCommandWithTag(out, 'textbf', 'strong') // R-151
  out = replaceCommandWithTag(out, 'textit', 'em') // R-152
  out = replaceCommandWithTag(out, 'underline', 'u') // R-153
  out = replaceCommandWithTag(out, 'texttt', 'code') // R-154
  out = replaceCommandWithTag(out, 'emph', 'em') // R-155
  out = replaceCommandWithTag(out, 'textsc', 'span', 'small-caps') // R-156
  out = replaceCommandWithTag(out, 'textsuperscript', 'sup') // R-157
  return out
}

// ─────────────────────────────────────────────────────────────────────────────────────────
//   ── DOMAIN SLOT ──  is owned by the domain-macro module (R-158 … R-175).
// ─────────────────────────────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────────────────────────────
// Slot 5 — manual TOC entries (R-176, )
// ─────────────────────────────────────────────────────────────────────────────────────────

/**
 * R-176 — `\addcontentsline{toc}{level}{title}` → an invisible marker span.
 *
 * **This is the ONLY source of TOC entries** (R-309): every heading this stage emits carries
 * `no-toc`, and S17 skips those. Level map: `subsection`→2, `subsubsection`→3, `paragraph`→4,
 * EVERYTHING ELSE→1 (so `chapter`, `section`, and a typo all land on level 1).
 *
 * Only `"` is escaped in the title (`&quot;`). `<`, `>` and `&` pass through into the
 * attribute unescaped (DF-9).
 */
export function processAddContentsLine(html: string): string {
  // R-176
  return html.replace(
    /\\addcontentsline\s*\{toc\}\s*\{([^}]+)\}\s*\{([^}]+)\}/g,
    (_match: string, levelStr: string, title: string) => {
      let level = 1
      const l = levelStr.trim()
      if (l === 'subsection') level = 2
      else if (l === 'subsubsection') level = 3
      else if (l === 'paragraph') level = 4
      const safeTitle = title.replace(/"/g, '&quot;')
      return `<span class="manual-toc-entry" data-level="${level}" data-title="${safeTitle}"></span>`
    },
  )
}

// ─────────────────────────────────────────────────────────────────────────────────────────
// Slot 6 — page style / counters / TOC placeholder (R-177 … R-181, )
// ─────────────────────────────────────────────────────────────────────────────────────────

/** R-177 … R-181. */
export function processPageStyleCommands(html: string): string {
  let out = html
  // PREAMBLE LINK: \thispagestyle{empty} inside \afficherTitre (preambuleV3.tex:219),
  out = out.replace(/\\thispagestyle\{[^}]*\}/g, '') // R-177
  out = out.replace(/\\pagestyle\{[^}]*\}/g, '') // R-178
  out = out.replace(/\\setcounter\{[^}]+\}\{[^}]+\}/g, '') // R-179
  out = out.replace(/\\addto\\[a-zA-Z]+\{[^}]*\}/g, '') // R-180
  // R-181  — substituted with the real TOC at S18.
  out = out.replace(/\\setcounter\{[^}]+\}\{[^}]+\}/g, '') // R-179
  // PREAMBLE LINK: \addto\captionsfrench{\renewcommand{\contentsname}{}} (preambuleV3.tex:238) —
  // "Masquer le titre Table des matières". NOT READ; this rule only deletes the token. The request
  // is nevertheless satisfied, by hard-coding: toc.ts never emits a heading above the TOC at all
  // (see buildTableOfContents). So the output is right and the link is absent — restoring a title
  // in toc.ts would silently contradict this preamble line.
  // /!\ In production this rule never fires on that line anyway: it sits before \begin{document}
  // and is sliced away at S5. It matters only when the preamble is pasted into the document box.
  out = out.replace(/\\addto\\[a-zA-Z]+\{[^}]*\}/g, '') // R-180
  // R-181 — substituted with the real TOC at S18.
  out = out.replace(/\\tableofcontents/g, '<nav class="toc-placeholder">[Table of Contents]</nav>')
  return out
}

// ─────────────────────────────────────────────────────────────────────────────────────────
// Slot 7 — sectioning (R-182 … R-185, )
// ─────────────────────────────────────────────────────────────────────────────────────────

/**
 * R-182 … R-185 — `\section` → `h1`, `\subsection` → `h2`, `\subsubsection` → `h3`,
 * `\paragraph` → `h4`. Starred variants (`\section*`) are accepted and produce identical
 * output — the star is not recorded anywhere.
 *
 * Every heading gets `no-toc`; when the title contains `\hfil`, `\hfill` or `\centering` the
 * class becomes `no-toc centered-heading` and the switch is stripped + `.trim()`-ed.
 *
 * PRESERVED QUIRKS:
 *   - `[^}]+` — a nested `{}` truncates the title: `\section{A {B} C}` → `<h1…>A {B</h1> C}`.
 *   - `[^}]+` also requires ≥1 char, so `\section{}` leaks verbatim.
 *   - No `s` flag is needed for multi-line titles because `[^}]` already spans newlines — so
 *     `\section{Titre\nDeux}` DOES become an `<h1>`. It is S17's collector regex (which has no
 *     `s` flag and uses `.`) that then fails to see it, producing a dead TOC link (spot-check
 *     #15). Reproduce the heading here; the dead link is S17's business.
 *   - `\section[short]{long}` is NOT matched (R-333) — the whole command leaks.
 *   - `\chapter`, `\part`, `\subparagraph` have no rule (R-332) and leak.
 */
export function processSections(html: string): string {
  const processHeading = (source: string, levelString: string, levelTag: string): string => {
    const regex = new RegExp(`\\\\${levelString}\\*?\\{([^}]+)\\}`, 'g')
    return source.replace(regex, (_match: string, content: string) => {
      // PREAMBLE LINK: \hfil — the house centring idiom, used in ten preamble macros (\Ntablet
      // :257, \glad :434, \arbsq :387, and the seven \appelC*) and written directly in headings
      // throughout the corpus. See the "DÉCISION DU 2026-08-31" note in preambuleV3.tex, at the
      // head of "Commandes de formats", and docs/HFIL-AND-CENTRING.md for the full analysis.
      //
      // READ AS AN INTENTION, NOT REPRODUCED — a deliberate substitution, owner ruling 2026-08-31.
      //
      // \hfil is not a centring command. It is stretchable space that absorbs whatever width is
      // left on the line it lands on, and it centres only by symmetry with the fill TeX appends at
      // the end of every paragraph (\parfillskip). Both fills must land on the SAME line to
      // balance. On one line they do, and XeLaTeX centres. On two they do not, and XeLaTeX gives
      // first line flush RIGHT, middle lines justified, last line flush LEFT — confirmed against
      // the real Kitáb-i-Aqdas PDF, 2026-08-31.
      //
      // The line below reads \hfil as "centre this", strips it, and centres the whole block. One
      // line: identical to the PDF. Two or more: deliberately different.
      //
      // WHY THAT WAS CHOSEN over reproducing LaTeX:
      //   • The intent is unambiguous; the letter is not. Every one of the ten preamble macros
      //     using \hfil means "centre this". Reproducing LaTeX means reproducing what the idiom
      //     does when it FAILS, which is not a result anyone chose.
      //   • 31 of the 32 \hfil headings in the corpus fit on one line at the reading width and are
      //     rendered identically. One 86-character heading is the sole divergence.
      //   • Exact reproduction was available and declined — width:fit-content + margin-inline:auto
      //     reproduces one and two lines by the same mechanism LaTeX uses. It is written out in
      //     docs/HFIL-AND-CENTRING.md §5.1. Read that before reopening this.
      //
      // /!\ \centering is NOT a substitution and needs no defending: it really does centre every
      // line in LaTeX, and text-align: center really does centre every line in CSS. The two are an
      // exact pair. It is only lumped in here because this one flag serves both — if they are ever
      // to differ, the split belongs HERE, not in the stylesheet, because by the time CSS runs both
      // have become the same class and the distinction is gone.
      const isCentered =
        content.includes('\\hfil') ||
        content.includes('\\hfill') ||
        content.includes('\\centering')

      let cleanContent = content
      if (isCentered) {
        cleanContent = cleanContent
          .replace(/\\hfil(l)?(\s+)?/g, '')
          .replace(/\\centering(\s+)?/g, '')
          .trim()
      }

      const className = isCentered ? 'no-toc centered-heading' : 'no-toc'
      return `<${levelTag} class="${className}">${cleanContent}</${levelTag}>`
    })
  }

  let out = html
  out = processHeading(out, 'section', 'h1') // R-182
  out = processHeading(out, 'subsection', 'h2') // R-183
  out = processHeading(out, 'subsubsection', 'h3') // R-184
  out = processHeading(out, 'paragraph', 'h4') // R-185
  return out
}

/**
 * `generateHeadingId` — the anchor slug scheme.
 *
 * DUPLICATE — `toc.ts` also exports `generateHeadingId`, and the two are behaviourally
 * identical (both verified against the oracle on the same inputs). It belongs to S17
 * `buildTableOfContents`; it is kept here only because it is the counterpart of
 * `processSections`. **Wire `toc.ts`'s copy, not this one**, and never `export *` both modules
 * from `index.ts` — the names collide.
 *
 * PRESERVED DEFECT (R-311, and worse than "ASCII fold"): `[^\w\s-]` is ASCII-only, so accented
 * letters are DELETED rather than transliterated — `Première partie` → `premire-partie-0`,
 * `Les Écrits` → `les-crits-0`, `Kitáb-i-Aqdas` → `kitb-i-aqdas-1`. HTML entities leak their
 * letters into the slug (`&nbsp;` → `nbsp`). An all-accent title collapses to `section-N`.
 */
export function generateHeadingId(text: string, index: number): string {
  const clean = text
    .replace(/<[^>]+>/g, '')
    .replace(/[^\w\s-]/g, '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-')

  return `${clean || 'section'}-${index}`
}

// ─────────────────────────────────────────────────────────────────────────────────────────
// Slot 8 — naive text styling duplicates (R-186 … R-190, )
// ─────────────────────────────────────────────────────────────────────────────────────────

/**
 * R-186 … R-190 — naive `[^}]*` duplicates of R-151…R-156, kept because they are in the
 * source and the port must be byte-identical.
 *
 * Unreachable in practice: `replaceCommandWithTag` already consumed every well-formed
 * occurrence at slot 3, and the leftovers it declined (unbalanced braces) do not match `[^}]*`
 * either. Note this run has NO `texttt` and NO `textsuperscript`, and it orders `emph` BEFORE
 * `underline` — the opposite of slot 3. Port the difference; do not tidy it.
 */
export function processNaiveTextStyling(html: string): string {
  let out = html
  out = out.replace(/\\textbf\{([^}]*)\}/g, '<strong>$1</strong>') // R-186
  out = out.replace(/\\textit\{([^}]*)\}/g, '<em>$1</em>') // R-187
  out = out.replace(/\\emph\{([^}]*)\}/g, '<em>$1</em>') // R-188
  out = out.replace(/\\underline\{([^}]*)\}/g, '<u>$1</u>') // R-189
  out = out.replace(/\\textsc\{([^}]*)\}/g, '<span class="small-caps">$1</span>') // R-190
  return out
}

// ─────────────────────────────────────────────────────────────────────────────────────────
// Slot 9 — font sizes (R-191 … R-203, )
// ─────────────────────────────────────────────────────────────────────────────────────────

/**
 * R-191 … R-203 — font sizes in BOTH forms.
 *
 * Braced form `\large{text}` → `<span class="large-text">text</span>` (six sizes; `[^}]*`, so
 * nested braces truncate). Bare switch form `\large` → deleted, because scope is not tracked.
 *
 * CONSEQUENCES THE PORT MUST PRESERVE:
 *   - R-348: the LaTeX group idiom `{\large grand}` leaves the braces behind → `{ grand}`.
 *     This is the mechanism behind the literal-brace leaks in the domain `\appel*`/`\ltr*`
 *     macros (C-7).
 *   - The strippers use `\b(?!\{)` and do NOT consume trailing whitespace, so the residue keeps
 *     a leading space (`\marginpar{\scriptsize 5}` → `<span class="marginpar"> 5</span>`, C-1).
 * R-198/R-199 run BEFORE the `\marginpar` size detection at /995, making those two
 *     branches unreachable. `\tiny` and `\footnotesize` have NO stripper here and therefore
 *     ARE reachable inside `\marginpar` — and leak verbatim everywhere else (R-325).
 *   - `\Huge` has neither a braced rule nor a stripper: it always leaks. `.Huge-text` CSS
 *     exists with no emitter.
 *   - `\b` means `\smallskip`, `\Largex`, `\hugely` are untouched (no boundary between two
 *     word characters).
 */
export function processFontSizes(html: string): string {
  let out = html
  out = out.replace(/\\scriptsize\{([^}]*)\}/g, '<span class="scriptsize">$1</span>') // R-191
  out = out.replace(/\\small\{([^}]*)\}/g, '<span class="small-text">$1</span>') // R-192
  out = out.replace(/\\large\{([^}]*)\}/g, '<span class="large-text">$1</span>') // R-193
  out = out.replace(/\\Large\{([^}]*)\}/g, '<span class="Large-text">$1</span>') // R-194
  out = out.replace(/\\LARGE\{([^}]*)\}/g, '<span class="LARGE-text">$1</span>') // R-195
  out = out.replace(/\\huge\{([^}]*)\}/g, '<span class="huge-text">$1</span>') // R-196
  out = out.replace(/\\normalsize/g, '') // R-197
  out = out.replace(/\\scriptsize\b(?!\{)/g, '') // R-198
  out = out.replace(/\\small\b(?!\{)/g, '') // R-199
  out = out.replace(/\\large\b(?!\{)/g, '') // R-200
  out = out.replace(/\\Large\b(?!\{)/g, '') // R-201
  out = out.replace(/\\LARGE\b(?!\{)/g, '') // R-202
  out = out.replace(/\\huge\b(?!\{)/g, '') // R-203
  return out
}

// ─────────────────────────────────────────────────────────────────────────────────────────
// Slot 10 — special characters and escapes (R-204 … R-211, )
// ─────────────────────────────────────────────────────────────────────────────────────────

/**
 * R-204 … R-211.
 *
 * R-204 `(?<!\\)\\&` — the negative lookbehind is MANDATORY: without it the second backslash
 * of a `\\&` row separator would be eaten. It still does not save the cell, because S12 later
 * splits the row on the `&` inside `&amp;` (DF-4).
 *
 * DF-6 (R-005 / G-11) LIVES HERE. S1's comment stripper declines to strip `\\%` because its
 * `(?<!\\)` lookbehind sees the escaped `%`; R-205 then eats the SECOND backslash. Net result:
 * `Test \\% pas commentaire` → `Test \% pas commentaire` — the comment text is published as
 * scripture, with a stray backslash in front of it. Keep it.
 *
 * DF-5 (R-206): `\$` → `$` is dead on arrival. S10 (`processInlineMath`) already treated the
 * escaped dollars as math delimiters and replaced everything between them with a KaTeX error
 * span, so there is usually no `\$` left for this rule to fix.
 *
 * DF-14 (R-211): `\textasciitilde` → `~`, which R-222 then turns into `&nbsp;`. That is why
 * `\arbsq` renders as an invisible blank line instead of an arabesque.
 *
 * NOT HANDLED — DF-10: `\oe \OE \ae \AE \ss \aa \o \l \i \j` and every accent command
 * (`\' \` \^ \" \~ \= \. \u \v \H \c \k \b \d \r \t`) have NO rule and print as raw LaTeX
 * (R-329/R-330). `c\oe ur` publishes as `c\oe ur`. Do not add rules for these.
 */
export function processSpecialCharacters(html: string): string {
  let out = html
  out = out.replace(/(?<!\\)\\&/g, '&amp;') // R-204
  out = out.replace(/\\%/g, '%') // R-205  — DF-6
  out = out.replace(/\\\$/g, '$') // R-206  — DF-5, too late
  out = out.replace(/\\#/g, '#') // R-207
  out = out.replace(/\\_/g, '_') // R-208
  out = out.replace(/\\{/g, '{') // R-209
  out = out.replace(/\\}/g, '}') // R-210
  out = out.replace(/\\textasciitilde/g, '~') // R-211  — then destroyed by R-222
  return out
}

// ─────────────────────────────────────────────────────────────────────────────────────────
// Slot 11 — French typography (R-212 … R-219, )
// ─────────────────────────────────────────────────────────────────────────────────────────

/**
 * R-212 … R-219 — guillemets and English quotes.
 *
 * All four guillemet openers emit `«` + `&nbsp;` (U+00AB then the ENTITY `&nbsp;`, never a
 * literal U+00A0); all four closers emit `&nbsp;` + `»`. Which entity: always `&nbsp;`, never
 * `&#8239;`/U+202F — DF-13 is the standing proposal to introduce the narrow no-break space,
 * and it is NOT implemented.
 *
 *   `\og`  → `«&nbsp;`   `\fg`  → `&nbsp;»`
 *   `<<`   → `«&nbsp;`   `>>`   → `&nbsp;»`
 *   `« ` (with whitespace) → `«&nbsp;`   ` »` → `&nbsp;»`
 *
 * `\og\s*` / `\fg\s*` / `<<\s*` swallow following whitespace INCLUDING newlines; `\s*>>` and
 * `\s+»` swallow preceding whitespace including newlines, so a `>>` that starts a line pulls
 * up onto the previous one.
 *
 * `«~A~»` — the reference preamble's own convention (CLAUDE.md §1, pre:81-83) — is NOT touched
 * here: `~` is not `\s`. It becomes `«&nbsp;A&nbsp;»` later, at R-222.
 *
 * `\og`/`\fg` are matched WITHOUT a trailing word boundary, so `\ogA` → `«&nbsp;A`.
 */
export function processFrenchTypography(html: string): string {
  let out = html
  out = out.replace(/\\og\s*/g, '«&nbsp;') // R-212
  out = out.replace(/\\fg\s*/g, '&nbsp;»') // R-213
  out = out.replace(/<<\s*/g, '«&nbsp;') // R-214
  out = out.replace(/\s*>>/g, '&nbsp;»') // R-215
  out = out.replace(/«\s+/g, '«&nbsp;') // R-216
  out = out.replace(/\s+»/g, '&nbsp;»') // R-217
  out = out.replace(/``/g, '"') // R-218
  out = out.replace(/''/g, '"') // R-219
  return out
}

// ─────────────────────────────────────────────────────────────────────────────────────────
// Slot 12 — dashes, symbols, dates, ordinals (R-220 … R-233, )
// ─────────────────────────────────────────────────────────────────────────────────────────

/**
 * R-220 … R-233.
 *
 * ORDER: `---` before `--` (else an em dash would become `–-`); `\LaTeX` before `\TeX` (else
 * `\LaTeX` would degrade to `\La` + the TeX logo); `\ldots` before `\dots`; `\No` before `\no`.
 * `----` → `—-`, because the first rule consumes three of the four hyphens.
 *
 * R-221 `--` → `–` runs at S11, i.e. BEFORE S12 reads list options, so `[label=--]` in an
 * `enumitem` option list can never match (R-288). Preserved.
 *
 * R-222 `~` → `&nbsp;` is global and unconditional. It is what turns the preamble's `«~…~»`
 * convention into correct guillemet spacing — and what destroys `\arbsq` (DF-14) and mangles
 * `\~n` into `\&nbsp;n` (DF-10; the accent command's argument is eaten by an unrelated rule).
 *
 * G-25, HALF TWO: R-227 `\today` renders **fr-FR** (`21 mars 2026`) in body text, while the
 * same token inside `\date{}` renders **en-US** via `processMaketitle`. A STRING replacement in
 * the source, reproduced as such.
 *
 * C-10 — ALL FOUR ORDINALS WORK. An earlier read-only analysis claimed `\iere` and `\iemes`
 * were shadowed by the shorter `\ier`/`\ieme`; that was wrong. `/\\ier\b/` cannot match inside
 * `\iere` because `r` and `e` are both word characters, so there is no boundary between them.
 * Verified against the oracle: `1\ier 1\iere 2\ieme 3\iemes` → `1<sup>er</sup> 1<sup>re</sup>
 * 2<sup>e</sup> 3<sup>es</sup>`.
 *
 * NOT HANDLED: `\textellipsis`, `\copyright`, `\S`, `\P`, `\dag`, `\ddag`, `\textbackslash` —
 * all leak verbatim.
 *
 * @param now injected clock. Defaults to `new Date()`, exactly as.
 */
export function processDashesAndSymbols(html: string, now: Date = new Date()): string {
  let out = html
  out = out.replace(/---/g, '—') // R-220
  out = out.replace(/--/g, '–') // R-221
  // PREAMBLE LINK: this rule is what actually delivers French punctuation spacing, and it is NOT
  // driven by \FBsetspaces{colon}/{thin} (preambuleV3.tex:142-143). Those two lines are parsed into
  out = out.replace(/~/g, '&nbsp;') // R-222
  out = out.replace(/\\ldots/g, '…') // R-223
  out = out.replace(/\\dots/g, '…') // R-224
  // R-225
  // translator typed a `~` there too. /!\ The same rule destroys \arbsq (DF-14): \textasciitilde
  // became `~` one slot earlier at R-211, and lands here as an invisible &nbsp;.
  out = out.replace(/~/g, '&nbsp;') // R-222
  out = out.replace(/\\ldots/g, '…') // R-223
  out = out.replace(/\\dots/g, '…') // R-224
  // R-225
  out = out.replace(/\\LaTeX/g, '<span class="latex-logo">L<sup>A</sup>T<sub>E</sub>X</span>')
  // R-226
  out = out.replace(/\\TeX/g, '<span class="tex-logo">T<sub>E</sub>X</span>')
  // R-227  — fr-FR. G-25.
  out = out.replace(
    /\\today/g,
    now.toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' }),
  )
  out = out.replace(/\\No(?![a-zA-Z])\s*/g, 'N°&nbsp;') // R-228
  out = out.replace(/\\no(?![a-zA-Z])\s*/g, 'n°&nbsp;') // R-229
  out = out.replace(/\\ier\b/g, '<sup>er</sup>') // R-230
  out = out.replace(/\\iere\b/g, '<sup>re</sup>') // R-231  — reachable (C-10)
  out = out.replace(/\\ieme\b/g, '<sup>e</sup>') // R-232
  out = out.replace(/\\iemes\b/g, '<sup>es</sup>') // R-233  — reachable (C-10)
  return out
}

// ─────────────────────────────────────────────────────────────────────────────────────────
// Slot 13 — line and page breaks (R-234 … R-241, )
// ─────────────────────────────────────────────────────────────────────────────────────────

/**
 * R-234 … R-241.
 *
 * `\\` itself is deliberately NOT converted here — S13 does it, after S12, so that
 * `tabular` can still split rows on `\\`. Only the `\\par` special case is handled now.
 *
 * `\par\b` → `<!--PAR_BREAK-->`, the marker S15 splits paragraphs on. `\b` keeps `\paragraph`,
 * `\parbox` and `\parskip` safe.
 *
 * G-8: `\newpage` mid-paragraph emits a `<div>` INSIDE a `<p>`, because `page-break` is absent
 * from the S15 skip list. Invalid nesting; browsers auto-close the `<p>`. Preserved.
 */
export function processLineAndPageBreaks(html: string): string {
  let out = html
  out = out.replace(/\\\\par\b/g, '<br>par') // R-234
  out = out.replace(/\\newline/g, '<br>') // R-235
  out = out.replace(/\\linebreak/g, '<br>') // R-236
  out = out.replace(/\\par\b/g, '<!--PAR_BREAK-->') // R-237
  out = out.replace(/\\newpage/g, PAGE_BREAK_HTML) // R-238
  out = out.replace(/\\clearpage/g, PAGE_BREAK_HTML) // R-239
  out = out.replace(/\\pagebreak/g, PAGE_BREAK_HTML) // R-240
  out = out.replace(/\\nopagebreak/g, '') // R-241
  return out
}

// ─────────────────────────────────────────────────────────────────────────────────────────
// Slot 14 — vertical spacing (R-242 … R-246, )
// ─────────────────────────────────────────────────────────────────────────────────────────

/**
 * R-242 … R-246.
 *
 * G-16 LIVES HERE. `\vspace` injects the captured length RAW — no `latexLengthToCSS` — while
 * `\hspace` (R-257, slot 15) converts it. So `\vspace{12pt}` → `margin-top: 12pt` but
 * `\hspace{12pt}` → `margin-left: 16px`, in the same document. Both are reproduced verbatim.
 *
 * `[^}]+` requires the brace to follow immediately: `\vspace {2em}` leaks. `\vspace{\baselineskip}`
 * emits the literal `margin-top: \baselineskip;`.
 *
 * G-9: these `<div>`s land inside `<p>`s all over the output (`\Tital`, `\Ntablet`, `\glad`,
 * `\arbsq`, every `\appel*`). Invalid nesting is pervasive, not exceptional.
 */
export function processVerticalSpacing(html: string): string {
  let out = html
  out = out.replace(/\\bigskip/g, '<div class="bigskip"></div>') // R-242
  out = out.replace(/\\medskip/g, '<div class="medskip"></div>') // R-243
  out = out.replace(/\\smallskip/g, '<div class="smallskip"></div>') // R-244
  out = out.replace(/\\vfill/g, '<div class="vfill"></div>') // R-245
  // R-246  — raw length, NOT converted. G-16.
  out = out.replace(
    /\\vspace\*?\{([^}]+)\}/g,
    (_match: string, size: string) => `<div style="margin-top: ${size};"></div>`,
  )
  return out
}

// ─────────────────────────────────────────────────────────────────────────────────────────
// Slot 15 — horizontal spacing (R-247 … R-257, )
// ─────────────────────────────────────────────────────────────────────────────────────────

/**
 * R-247 … R-257.
 *
 * `\quad` before `\qquad` is safe: `/\\quad/` cannot match inside `\qquad` (the `quad` there is
 * preceded by `q`, not a backslash).
 *
 * DF-3 WAS HERE, and is FIXED — 2026-08-31. R-253 used to be a bare `/\\ /g` → `' '`, which
 * runs at S11, long before S13 turns `\\` into `<br>` and S12 splits table rows on it. Reading
 * left to right, that pattern found "backslash then space" at the SECOND backslash of `\\ `
 * and swallowed both. One backslash was left over: the line break never happened and the
 * orphan was **published into scripture**. Two symptoms, one cause — merged table rows, and a
 * stray `\` in running text. See `docs/DEFERRED-FIXES.md` DF-3.
 *
 * The counting rule below is LaTeX's own: pair the backslashes up from the left, and only an
 * ODD one left over is a real `\ ` (explicit interword space). An even run is line breaks and
 * the space after it is just whitespace. The stage order is untouched — reordering it would
 * have changed rendered output across every document, which is why it was rejected.
 *
 * Note `\-`, `\/` and `\@` are NOT matched by this rule (R-335) and leak.
 *
 * G-17: `\enspace` is replaced twice ( and ). The second call is a no-op — nothing
 * matches by then. Both lines are kept so the rule sequence is a faithful transcript.
 *
 * R-257 `\hspace` DOES run its length through the converter, unlike `\vspace` (G-16).
 */
export function processHorizontalSpacing(html: string): string {
  let out = html
  out = out.replace(/\\quad/g, '&emsp;') // R-247
  out = out.replace(/\\qquad/g, '&emsp;&emsp;') // R-248
  out = out.replace(/\\,/g, '&thinsp;') // R-249
  out = out.replace(/\\;/g, '&ensp;') // R-250
  out = out.replace(/\\:/g, '&ensp;') // R-251
  out = out.replace(/\\!/g, '') // R-252
  // R-253 — `\ ` (explicit interword space) → a plain space. DF-3 FIXED here, 2026-08-31.
  //
  //   (?<!\\)        do not start in the middle of a backslash run
  //   ((?:\\\\)*)    step over whole `\\` pairs — these are LINE BREAKS, keep them ($1)
  //   \\             …then a single leftover backslash
  //   ' '            …followed by the space it binds to
  //
  // Odd run → the last backslash is a real `\ ` and is converted, exactly as before.
  // Even run → every backslash is half of a `\\`; nothing is touched and S13 makes the <br>s.
  //
  // /!\ Do NOT "simplify" this back to /\\ /g. That is the DF-3 defect: on `\\ ` it matched the
  // second backslash plus the space, leaving one behind for S13 to miss and the page to show.
  // Verified against all 74 fixtures in both modes — only the two DF-3 fixtures change.
  out = out.replace(/(?<!\\)((?:\\\\)*)\\ /g, '$1 ') // R-253
  out = out.replace(/\\enskip/g, '&ensp;') // R-254
  out = out.replace(/\\enspace/g, '&ensp;') // R-255
  out = out.replace(/\\enspace/g, '&ensp;') // R-256  — duplicate, no-op (G-17)
  // R-257  — length IS converted. G-16.
  out = out.replace(
    /\\hspace\*?\{([^}]+)\}/g,
    (_match: string, size: string) => `<span style="margin-left: ${latexLengthToCSS(size)};"></span>`,
  )
  return out
}

// ─────────────────────────────────────────────────────────────────────────────────────────
// Slot 16 — alignment (R-258 … R-263, )
// ─────────────────────────────────────────────────────────────────────────────────────────

/**
 * R-258 … R-263.
 *
 * R-258 MUST run first: `\noindent\hfil` / `\noindent\hfill` is the house centring idiom (every
 * `\appelC*`, `\Ntablet`, `\arbsq` and `\glad` in the reference preamble uses it) and collapses
 * to a single `<span class="center-line"></span>`, swallowing the whitespace on both sides. If
 * `\hfill` or `\noindent` ran first the idiom would decompose into two spans and lose the
 * centring.
 *
 * `\hfill` before `\hfil` — the reverse order would leave a stray `l`.
 *
 * `\centering`, `\raggedright`, `\raggedleft` are deleted outright; the whitespace around them
 * is left behind.
 */
export function processAlignment(html: string): string {
  let out = html
  // R-258  — must precede R-259/R-260/R-264.
  out = out.replace(/\\noindent\s*\\hfill?\s*/g, '<span class="center-line"></span>')
  out = out.replace(/\\hfill/g, '<span class="hfill"></span>') // R-259
  out = out.replace(/\\hfil/g, '<span class="hfil"></span>') // R-260
  out = out.replace(/\\centering/g, '') // R-261
  out = out.replace(/\\raggedright/g, '') // R-262
  out = out.replace(/\\raggedleft/g, '') // R-263
  return out
}

// ─────────────────────────────────────────────────────────────────────────────────────────
// Slot 17 — indentation (R-264 … R-266, )
// ─────────────────────────────────────────────────────────────────────────────────────────

/**
 * R-264 … R-266.
 *
 * `\noindent\s*` consumes the following whitespace; `\indent\s*` likewise but emits nothing;
 * `\leavevmode` is deleted WITHOUT consuming whitespace, so `\leavevmode X` leaves ` X`.
 */
export function processIndentation(html: string): string {
  let out = html
  out = out.replace(/\\noindent\s*/g, '<span class="noindent"></span>') // R-264
  out = out.replace(/\\indent\s*/g, '') // R-265
  out = out.replace(/\\leavevmode/g, '') // R-266
  return out
}

// ─────────────────────────────────────────────────────────────────────────────────────────
// Slot 18 — margin notes (R-267 … R-268, )
// ─────────────────────────────────────────────────────────────────────────────────────────

/**
 * R-267, R-268 — `\marginpar{…}` → `<span class="marginpar…">…</span>`.
 *
 * Standard LaTeX, not a domain macro — but it is the load-bearing rule for verse numbering in
 * Mode A, because the preamble rewrites `\Nverset{n}` into `\marginpar{\scriptsize n}` at S7.
 *
 * DUPLICATE — `notes.ts` already exports this as `processMarginpar` (R-267) +
 * `removeReverseMarginpar` (R-268), behaviourally identical. **Wire `notes.ts`'s pair, not
 * this one**; it is kept here only so slot 18 is not a hole if that module moves. Never
 * `export *` both modules from `index.ts` — `processMarginpar` collides.
 *
 * `if / else if` chain, FIRST match wins, and the switch is removed with `\s*` (which DOES eat
 * the trailing space):
 *   `\tiny`         → ` tiny-text`     — REACHABLE (no stripper at slot 9)
 *   `\scriptsize`   → ` scriptsize`    — UNREACHABLE (R-198 already deleted it)
 *   `\footnotesize` → ` footnotesize`  — REACHABLE (no stripper)
 *   `\small`        → ` small-text`    — UNREACHABLE (R-199 already deleted it)
 *
 * C-1/C-9: because slot 9's strippers do NOT consume trailing whitespace, the residue of a
 * stripped `\scriptsize`/`\small` keeps a LEADING SPACE and lands in the span body:
 * `\marginpar{\scriptsize 5}` → `<span class="marginpar"> 5</span>`, whereas
 * `\marginpar{\tiny 6}` → `<span class="marginpar tiny-text">6</span>`. Both are observable.
 *
 * `[^}]+` — nested braces break it: `\marginpar{a{b}c}` → `<span class="marginpar">a{b</span>c}`.
 */
export function processMarginpar(html: string): string {
  let out = html
  // R-267
  out = out.replace(/\\marginpar\{([^}]+)\}/g, (_match: string, content: string) => {
    let processedContent = content
    let extraClass = ''

    if (processedContent.includes('\\tiny')) {
      extraClass = ' tiny-text'
      processedContent = processedContent.replace(/\\tiny\s*/g, '')
    } else if (processedContent.includes('\\scriptsize')) {
      extraClass = ' scriptsize'
      processedContent = processedContent.replace(/\\scriptsize\s*/g, '')
    } else if (processedContent.includes('\\footnotesize')) {
      extraClass = ' footnotesize'
      processedContent = processedContent.replace(/\\footnotesize\s*/g, '')
    } else if (processedContent.includes('\\small')) {
      extraClass = ' small-text'
      processedContent = processedContent.replace(/\\small\s*/g, '')
    }

    return `<span class="marginpar${extraClass}">${processedContent}</span>`
  })
  out = out.replace(/\\reversemarginpar/g, '') // R-268
  return out
}

// ─────────────────────────────────────────────────────────────────────────────────────────
//   ── APPARATUS SLOT ──  (R-269 \afficherEndNotes, R-270 \theendnotes,
//      R-271 \endnote fallback) is owned by the footnote/endnote module.
// ─────────────────────────────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────────────────────────────
// Slot 20 — labels and references (R-272 … R-274, )
// ─────────────────────────────────────────────────────────────────────────────────────────

/**
 * R-272 … R-274. `\label` is deleted; `\ref`/`\cite` become inert bracketed spans — they are
 * NOT hyperlinks and nothing ever resolves them. No hyperlink support of any kind exists in
 * this converter (R-338: `\url` and `\href` leak verbatim).
 */
export function processLabelsAndRefs(html: string): string {
  let out = html
  out = out.replace(/\\label\{[^}]+\}/g, '') // R-272
  out = out.replace(/\\ref\{([^}]+)\}/g, '<span class="ref">[$1]</span>') // R-273
  out = out.replace(/\\cite\{([^}]+)\}/g, '<span class="cite">[$1]</span>') // R-274
  return out
}

// ─────────────────────────────────────────────────────────────────────────────────────────
// Slot 21 — residual preamble-only commands (R-275 … R-279, )
// ─────────────────────────────────────────────────────────────────────────────────────────

/**
 * R-275 … R-279 — the last sweep, for preamble commands that reached the body.
 *
 * WHEN THIS STAGE MATTERS AT ALL. In the normal case it does almost nothing: the preamble sits
 * before `\begin{document}` and S5 slices it away long before slot 21. It carries real weight in
 * exactly two situations — a document whose `\begin{document}` is missing or mistyped, and
 * **previewing a preamble on its own**, which is how the two leaks below were found. A preamble
 * file contains no `\begin{document}` of its own (the only occurrences in `preambuleV3.tex` are
 * inside comments, stripped at S1), so R-077 makes the whole file the body and every command here
 * is all that stands between the preamble and the page.
 *
 * ✅ FIXED 2026-09-01 — the two that reached the page:
 *   R-277 `\geometry` was `\\geometry\{[^}]+\}`, and `[^}]+` stops at the first `}`. The real
 *         preamble writes `\geometry{paper=a4paper,total={160mm,247mm},inner=25mm,top=25mm}`, so
 *         the rule deleted up to the nested group's brace and **published the tail**,
 *         `,inner=25mm,top=25mm}`, as visible text. Now {@link removeCommand}, which is balanced.
 *   R-351 `\FBsetspaces` had **no rule at all** and leaked whole —
 *         `\FBsetspaces{colon}{0.5}{0.1}{0.1}` twice over. Its values are read into CSS variables
 *         that nothing consumes (DF-13 / RK-38); this rule only stops the token printing. Four
 *         arguments, hence the arity argument added to `removeCommand`.
 *
 * PRESERVED DEFECT:
 *   R-276 `\renewcommand\{[^}]+\}(\[[^\]]*\])?\{[^}]*\}` — `[^}]*` cannot span a nested-brace
 *         body, so `\renewcommand{\x}{a{b}c}` is left in the text. UNTOUCHED deliberately: the
 *         preamble's own `\renewcommand{\citation}[1]{\begin{quote}#1\end{quote}}` has a nested
 *         brace, so "fixing" this would delete a macro definition that S7 needs. It is only
 *         reachable when the preamble is the body, where the definition is inert anyway.
 */
export function processPreambleOnlyCommands(html: string): string {
  let out = html
  out = out.replace(/\\setlength\{[^}]+\}\{[^}]+\}/g, '') // R-275
  out = out.replace(/\\renewcommand\{[^}]+\}(\[[^\]]*\])?\{[^}]*\}/g, '') // R-276
  out = removeCommand(out, 'geometry') // R-277 — balanced; see the note above
  out = removeCommand(out, 'FBsetspaces', 4) // R-351
  out = out.replace(/\\linespread\{[^}]+\}/g, '') // R-278
  // PREAMBLE LINK: preambuleV3.tex declares \sloppy, which in LaTeX loosens how far the
  // typesetter may stretch interword spaces before letting a line overrun the margin.
  // Nothing here implements that; this rule only DELETES the token so it cannot print as
  // visible text. Justification itself is hard-coded in css.ts as
  // `p { text-align: justify !important }` and cannot be driven from the preamble.
  // NOT REACHED in the normal case: the preamble sits before \begin{document} and is sliced
  // away at S5 (index.ts) before this stage runs. It fires only when \sloppy appears in the
  // BODY, or when \begin{document} is absent and the whole file becomes the body.
  // /!\ None of the 10 books loaded on 2026-08-14 writes \sloppy in its body — but the corpus
  // is incomplete. Do not conclude the case never arises, and do not delete this rule.
  out = out.replace(/\\sloppy/g, '') // R-279
  return out
}

// ─────────────────────────────────────────────────────────────────────────────────────────
// The order contract
// ─────────────────────────────────────────────────────────────────────────────────────────

/**
 * The full S11 sequence as slots, for the coordinator that composes
 * `processCommands`. `owner: 'generic'` slots are the functions above, in this exact order;
 * `owner: 'domain'` / `'apparatus'` slots belong to sibling modules and must be interleaved at
 * the positions shown. Deviating from this order changes the emitted scripture.
 */
export const S11_ORDER = [
  { slot: 1, owner: 'generic', fn: 'removePreambleCommands', rules: 'R-142…R-149', lc: '623-630' },
  { slot: 2, owner: 'generic', fn: 'processMaketitle', rules: 'R-150', lc: '633-666' },
  { slot: 3, owner: 'generic', fn: 'processTextFormatting', rules: 'R-151…R-157', lc: '670-676' },
  { slot: 4, owner: 'domain', fn: 'ts / appel* / citation / Nverset / afficherTitre / afficherSommaire', rules: 'R-158…R-175', lc: '678-803' },
  { slot: 5, owner: 'generic', fn: 'processAddContentsLine', rules: 'R-176', lc: '806-816' },
  { slot: 6, owner: 'generic', fn: 'processPageStyleCommands', rules: 'R-177…R-181', lc: '818-822' },
  { slot: 7, owner: 'generic', fn: 'processSections', rules: 'R-182…R-185', lc: '828-851' },
  { slot: 8, owner: 'generic', fn: 'processNaiveTextStyling', rules: 'R-186…R-190', lc: '855-859' },
  { slot: 9, owner: 'generic', fn: 'processFontSizes', rules: 'R-191…R-203', lc: '862-875' },
  { slot: 10, owner: 'generic', fn: 'processSpecialCharacters', rules: 'R-204…R-211', lc: '883-890' },
  { slot: 11, owner: 'generic', fn: 'processFrenchTypography', rules: 'R-212…R-219', lc: '893-902' },
  { slot: 12, owner: 'generic', fn: 'processDashesAndSymbols', rules: 'R-220…R-233', lc: '905-921' },
  { slot: 13, owner: 'generic', fn: 'processLineAndPageBreaks', rules: 'R-234…R-241', lc: '925-934' },
  { slot: 14, owner: 'generic', fn: 'processVerticalSpacing', rules: 'R-242…R-246', lc: '937-943' },
  { slot: 15, owner: 'generic', fn: 'processHorizontalSpacing', rules: 'R-247…R-257', lc: '946-963' },
  { slot: 16, owner: 'generic', fn: 'processAlignment', rules: 'R-258…R-263', lc: '967-973' },
  { slot: 17, owner: 'generic', fn: 'processIndentation', rules: 'R-264…R-266', lc: '976-978' },
  { slot: 18, owner: 'generic', fn: 'processMarginpar', rules: 'R-267…R-268', lc: '981-1002' },
  { slot: 19, owner: 'apparatus', fn: 'afficherEndNotes / theendnotes / endnote', rules: 'R-269…R-271', lc: '1006-1009' },
  { slot: 20, owner: 'generic', fn: 'processLabelsAndRefs', rules: 'R-272…R-274', lc: '1012-1014' },
  { slot: 21, owner: 'generic', fn: 'processPreambleOnlyCommands', rules: 'R-275…R-279', lc: '1017-1021' },
] as const
